function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _possibleConstructorReturn(self, call) {
  if (call && (typeof call === "object" || typeof call === "function")) {
    return call;
  }

  return _assertThisInitialized(self);
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  if (superClass) _setPrototypeOf(subClass, superClass);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

import React from 'react';
import PropTypes from 'prop-types';
/**
 * combinedError is a higher-order function that takes in an ErrorComponent and
 * does some magic to ensure that if multiples of that ErrorComponent are rendered
 * on the screen at the same time, only one appears in the DOM.
 *
 * The reason this is necessary is because sometimes multiple things fail on the
 * same page, but you don't want to bombard a user with many error messages, so
 * this component manages that for you. It will render one component, and if the
 * retry handler is called from that component, it will attempt to retry each of
 * the combined retry handlers.
 *
 * Example:
 *   const CombinedError1 = combinedError(Error)
 *   const CombinedError2 = combinedError(Error)
 *   const CombinedError3 = combinedError(Error)
 * ..............................................
 *   render() {
 *     return (
 *       <div>
 *         <CombinedError1 retry={() => console.log('Retry CombinedError1!')} />
 *         <CombinedError2 retry={() => console.log('Retry CombinedError2!')} />
 *         <CombinedError3 retry={() => console.log('Retry CombinedError3!')} />
 *       </div>
 *     )
 *   }
 *
 * Actual output:
 *   <div>
 *     <Error retry={...} />
 *     {null}
 *     {null}
 *   </div>
 *
 * Output from calling retry:
 *   Retry CombinedError1!
 *   Retry CombinedError2!
 *   Retry CombinedError3!
 */
// create dictionary of memoized CombinedErrors

var combinedErrors = {}; // memoize CombinedErrors by component name

export default function combinedError(ErrorComponent) {
  var componentName = ErrorComponent.name;

  if (!combinedErrors[componentName]) {
    combinedErrors[componentName] = getCombinedError(ErrorComponent);
  }

  return combinedErrors[componentName];
}

function getCombinedError(ErrorComponent) {
  /**
   * CombinedError holds the state of the combined error. It manages the list
   * of retry handlers, and whether or not an instance of it is currently in use.
   *
   * It also passes a retry handler down into the wrapped ErrorComponent that
   * loops through and calls each of the retry handlers currently managed by it.
   */
  var CombinedError =
  /*#__PURE__*/
  function (_React$Component) {
    _inherits(CombinedError, _React$Component);

    function CombinedError() {
      var _getPrototypeOf2;

      var _this;

      _classCallCheck(this, CombinedError);

      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(CombinedError)).call.apply(_getPrototypeOf2, [this].concat(args))); // when this component is rendered, set inUse to true to signal to other
      // wrapped ErrorComponents that they don't need to render

      _defineProperty(_assertThisInitialized(_this), "handleRetry", function () {
        // when the rendered ErrorComponent calls retry, call each of the combined
        // errors' retry handlers
        CombinedError.retryHandlers.forEach(function (retry) {
          return retry();
        });
      });

      CombinedError.inUse = true;
      return _this;
    }

    _createClass(CombinedError, [{
      key: "componentWillUnmount",
      value: function componentWillUnmount() {
        // when this component is unmounted, reset the state so it can be
        // re-initialized later with a clean slate
        CombinedError.retryHandlers = [];
        CombinedError.inUse = false;
      }
    }, {
      key: "render",
      value: function render() {
        return React.createElement(ErrorComponent, Object.assign({}, this.props, {
          retry: this.handleRetry
        }));
      }
    }]);

    return CombinedError;
  }(React.Component);
  /**
   * CombinedErrorInstance is the actual component returned from this HoC. Any
   * time a new instance is created, it adds it's retry handler to the list of
   * retry handlers on the CombinedError class. It also checks to see if
   * CombinedError is already in use, and determines if it should render
   * accordingly.
   *
   * If CombinedError is already in use (mounted) then it doesn't need to render
   * the ErrorComponent again, so it returns null from render.
   */


  _defineProperty(CombinedError, "retryHandlers", []);

  _defineProperty(CombinedError, "inUse", false);

  var CombinedErrorInstance =
  /*#__PURE__*/
  function (_React$Component2) {
    _inherits(CombinedErrorInstance, _React$Component2);

    function CombinedErrorInstance(props) {
      var _getPrototypeOf3;

      var _this2;

      _classCallCheck(this, CombinedErrorInstance);

      for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        args[_key2 - 1] = arguments[_key2];
      }

      _this2 = _possibleConstructorReturn(this, (_getPrototypeOf3 = _getPrototypeOf(CombinedErrorInstance)).call.apply(_getPrototypeOf3, [this, props].concat(args)));
      CombinedError.retryHandlers.push(props.retry);
      if (!CombinedError.inUse) _this2.shouldRender = true;
      return _this2;
    }

    _createClass(CombinedErrorInstance, [{
      key: "render",
      value: function render() {
        if (!this.shouldRender) return null;
        return React.createElement(CombinedError, this.props);
      }
    }]);

    return CombinedErrorInstance;
  }(React.Component);

  _defineProperty(CombinedErrorInstance, "propTypes", {
    retry: PropTypes.func.isRequired
  });

  return CombinedErrorInstance;
}